<template>
  <div>
    <card-header
      :title="heading"
      :description="subheading"
      :infoConfig="$CONSTANTS.infoConfig.campaignStatus"
    >
      <template #icon>
        <icon launch xlarge />
      </template>
      <template #subtitle>
        <countdown
          v-if="status === $CONSTANTS.campaignStatusTypes.scheduled.value"
          :to="nextStartDate"
        />
      </template>
    </card-header>
  </div>
</template>

<script>
import { format, addDays, isValid } from 'date-fns';
import inRange from 'lodash/inRange';
import store from '@/store';
import { isIdNew } from '../../utils/id_utils';
import { parseDateTime, getDateTime, withTimezone } from '../../utils/date_utils';
import Countdown from './Countdown.vue';
import CardHeader from '@/components/global/CardHeader.vue';

export default {
  name: 'campaign-status',

  components: { Countdown, CardHeader },

  props: {
    campaign: { type: Object, required: true },
  },

  data() {
    return {
      now: withTimezone(new Date(), store.getTimeZone()),
    };
  },

  created() {
    if (this.campaign.id && !isIdNew(this.campaign.id)) {
      this.interval = setInterval(() => {
        this.now = withTimezone(new Date(), store.getTimeZone());
      }, 1000);
    }

    this.updateStatus();
  },

  destroyed() {
    clearInterval(this.interval);
  },

  computed: {
    heading() {
      switch (this.status) {
        case this.$CONSTANTS.campaignStatusTypes.scheduled.value:
          return this.$t('cms.campaignstatus.CampaignWillStartInTitle');
        case this.$CONSTANTS.campaignStatusTypes.running.value:
          return this.$t('cms.campaignstatus.CampaignIsRunningTitle');
        default:
          return this.$t('cms.campaign.StartCampaignTitle');
      }
    },

    subheading() {
      switch (this.status) {
        case this.$CONSTANTS.campaignStatusTypes.scheduled.value:
          return this.$t('cms.campaignstatus.CampaignWillStartInText');
        case this.$CONSTANTS.campaignStatusTypes.running.value:
          return this.$t('cms.campaignstatus.CampaignIsRunningText');
        default:
          return this.$t('cms.campaign.StartCampaignText');
      }
    },

    startDate() {
      return new Date(parseDateTime(this.startDateTime).date);
    },

    startDateTime() {
      return getDateTime(this.campaign.startDate, this.campaign.startTime);
    },

    isCampaignSaved() {
      return this.campaign.id && !isIdNew(this.campaign.id);
    },

    status() {
      if (!this.isCampaignSaved) {
        return this.$CONSTANTS.campaignStatusTypes.new.value;
      }

      const { startTime, endTime, daysActive } = this.campaign;
      const isInRange = this.getIsInRange(this.now, startTime, endTime, daysActive);

      if (this.startDateTime && (this.startDateTime > this.now
        || !isInRange
        || !isValid(this.startDateTime))
      ) {
        return this.$CONSTANTS.campaignStatusTypes.scheduled.value;
      }

      return this.$CONSTANTS.campaignStatusTypes.running.value;
    },

    nextStartDate() {
      const daysAreSelected = this.campaign.daysActive.length > 0;
      const isScheduled = this.status === this.$CONSTANTS.campaignStatusTypes.scheduled.value;

      if (!daysAreSelected || !isValid(this.startDate) || !this.isCampaignSaved || !isScheduled) {
        return null;
      }

      const daysActive = [...this.campaign.daysActive];
      daysActive.sort();

      // Get which the date which we should start searching for the next available day.
      // Today or startDate, whichever is later
      const searchFromDate = this.now < this.startDateTime
        ? this.startDate
        : this.now;

      // Add an extra weekday if current time passed end start time
      const isOvernight = this.campaign.startTime.replace(':') > this.campaign.endTime.replace(':');
      const endTime = isOvernight ? this.campaign.startTime : this.campaign.endTime;
      const searchFromTime = format(searchFromDate, 'HH:mm');
      const finishedToday = searchFromTime.replace(':') >= endTime.replace(':');
      const fromWeekday = searchFromDate.getDay();

      const nextActiveWeekday = daysActive.find(d => d >= fromWeekday + finishedToday)
          // If no weekday is found, pick the first (of next week).
          || Math.min(...daysActive);

      let differenceInDays = nextActiveWeekday - fromWeekday;

      if (differenceInDays < 0 + finishedToday) {
        // Since we dont want to go back in time, make it next week
        differenceInDays += 7;
      }

      const { endDate, startTime } = this.campaign;

      const nextStartDate = getDateTime(addDays(searchFromDate, differenceInDays), startTime);

      if (nextStartDate > getDateTime(endDate, endTime)) { return null; }

      return nextStartDate;
    },
  },

  watch: {
    status() {
      this.updateStatus();
    },
  },

  methods: {
    updateStatus() {
      this.$emit('status-update', this.status);
    },

    getIsInRange(date, startTime, endTime, daysActive = [0, 1, 2, 3, 4, 5, 6]) {
      let weekDayOfNow = date.getDay();
      let start = getDateTime(date, this.campaign.startTime);
      let end = getDateTime(date, this.campaign.endTime);

      const isOvernight = start > end;

      if (isOvernight) {
        const checkFromYesterday = date < end;

        if (checkFromYesterday) {
          // Check start time yesterday till end time today
          start = addDays(start, -1);
          weekDayOfNow = weekDayOfNow
            ? weekDayOfNow - 1
            : 6; // Make sure we dont go -1
        } else {
          // Else check start time today till end date tomorrow
          end = addDays(end, 1);
        }
      }

      const isActiveWeekday = daysActive.includes(weekDayOfNow);
      const isInRange = inRange(date, start, end);
      return isInRange && isActiveWeekday;
    },
  },
};

</script>
